// ─── General helpers ──────────────────────────────────────────────────────────

export function uid() {
  return Math.random().toString(36).slice(2, 9);
}

// Returns the merged style for the given breakpoint.
// Desktop = el.style (base). Tablet/mobile = el.style merged with el.responsiveStyles[bp].
export function getEffectiveStyle(el, bp) {
  if (!bp || bp === 'desktop') return el.style || {};
  return { ...(el.style || {}), ...(el.responsiveStyles?.[bp] || {}) };
}

// Sets a clean drag ghost from the element's current DOM node.
export function applyDragPreview(e, node) {
  const target = node || e.currentTarget;
  if (!target) return;
  const rect = target.getBoundingClientRect();

  const clone = target.cloneNode(true);
  Object.assign(clone.style, {
    position:      'fixed',
    left:          '-9999px',
    top:           '0px',
    width:         rect.width  + 'px',
    height:        rect.height + 'px',
    pointerEvents: 'none',
    opacity:       '1',
    borderRadius:  '6px',
    boxShadow:     '0 8px 24px rgba(0,0,0,0.18)',
    background:    'white',
    zIndex:        '99999',
    margin:        '0',
  });
  clone.style.outline = 'none';
  clone.querySelectorAll('*').forEach(c => { c.style.outline = 'none'; });

  document.body.appendChild(clone);
  e.dataTransfer.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top);
  setTimeout(() => {
    if (document.body.contains(clone)) document.body.removeChild(clone);
  }, 200);
}

// ─── Tree helpers — work at ANY nesting depth ─────────────────────────────────

/**
 * Remove an element anywhere in the tree (section top-level OR nested at any depth).
 * Returns [newSections, removedEl].
 */
export function removeEl(sections, elId) {
  let removed = null;
  function filterChildren(children) {
    return children
      .filter(c => { if (c.id === elId) { removed = c; return false; } return true; })
      .map(c => c.type === 'container' ? { ...c, children: filterChildren(c.children || []) } : c);
  }
  const next = sections.map(s => ({ ...s, elements: filterChildren(s.elements) }));
  return [next, removed];
}

/**
 * Insert `el` into a container's children at `idx` — searches all depths.
 */
export function insertIntoContainer(sections, containerId, el, idx) {
  function insertInChildren(children) {
    return children.map(c => {
      if (c.id === containerId) {
        const ch = [...(c.children || [])];
        ch.splice(idx, 0, el);
        return { ...c, children: ch };
      }
      if (c.type === 'container') return { ...c, children: insertInChildren(c.children || []) };
      return c;
    });
  }
  return sections.map(s => ({ ...s, elements: insertInChildren(s.elements) }));
}

/**
 * Insert `el` into a section's top-level elements at `idx`.
 */
export function insertIntoSection(sections, sectionId, el, idx) {
  return sections.map(s => {
    if (s.id !== sectionId) return s;
    const els = [...s.elements];
    els.splice(idx, 0, el);
    return { ...s, elements: els };
  });
}

/**
 * Update a single element anywhere in the tree by id.
 */
export function updateEl(sections, elId, updater) {
  function updateInChildren(children) {
    return children.map(c => {
      if (c.id === elId) return updater(c);
      if (c.type === 'container') return { ...c, children: updateInChildren(c.children || []) };
      return c;
    });
  }
  return sections.map(s => ({ ...s, elements: updateInChildren(s.elements) }));
}

/**
 * Reorder children inside any container at any depth.
 */
export function reorderInContainer(sections, containerId, fromIdx, toIdx) {
  function reorderInChildren(children) {
    return children.map(c => {
      if (c.id === containerId) {
        const ch = [...(c.children || [])];
        const [moved] = ch.splice(fromIdx, 1);
        ch.splice(toIdx, 0, moved);
        return { ...c, children: ch };
      }
      if (c.type === 'container') return { ...c, children: reorderInChildren(c.children || []) };
      return c;
    });
  }
  return sections.map(s => ({ ...s, elements: reorderInChildren(s.elements) }));
}

/**
 * Returns true if `ancestorId` is an ancestor of (or equal to) `nodeId`
 * anywhere in the sections tree. Used to prevent dropping a container into itself.
 */
export function isDescendantOrSelf(sections, ancestorId, nodeId) {
  if (ancestorId === nodeId) return true;
  function check(children) {
    for (const c of children) {
      if (c.id === ancestorId) return containsId(c.children || [], nodeId);
      if (c.type === 'container' && check(c.children || [])) return true;
    }
    return false;
  }
  function containsId(children, id) {
    for (const c of children) {
      if (c.id === id) return true;
      if (c.type === 'container' && containsId(c.children || [], id)) return true;
    }
    return false;
  }
  for (const s of sections) {
    if (check(s.elements)) return true;
  }
  return false;
}

export const makeHeaderSection = () => ({
  id: uid(),
  label: 'Header',
  type: 'header',
  elements: [],
  style: {},
  headerConfig: {
    template: 'classic',
    logoSrc: '',
    logoText: 'Event',
    logoHeight: 36,
    logoAlt: '',
    links: [],
    showLogin: true,
    loginLabel: 'Login',
    showRegister: true,
    registerLabel: 'Register Now',
    style: {
      background: '#ffffff',
      linkColor: '#1a1a2e',
      loginColor: '#1075BA',
      registerBg: '#1075BA',
      registerColor: '#ffffff',
      pillBg: '#ffffff',
      height: 72,
    },
  },
});


export const makeFooterSection = () => ({
  id: uid(),
  label: 'Footer',
  type: 'footer',
  elements: [],
  style: {},
  footerConfig: {
    template: 'columns',
    logoSrc: '',
    logoText: 'Event',
    logoHeight: 32,
    logoAlt: '',
    tagline: '',
    links: [],
    copyrightText: `© ${new Date().getFullYear()} My Company. All rights reserved.`,
    style: {
      background: '#ffffff',
      logoColor: '#1a1a2e',
      linkColor: '#5a6474',
      linkHoverColor: '#1075BA',
      copyrightColor: '#9ea8b3',
      borderColor: '#e8edf2',
      paddingX: '48px',
      paddingY: '40px',
    },
  },
});
 
export const makeInitialSections = () => [
  makeHeaderSection(),
  {
    id: uid(), label: 'Section',
    elements: [],
    style: { padding: '60px 48px', background: '#fff' },
  },
  makeFooterSection(),
];
 